// Daily Storytelling Tips for Art of Story

const storytellingTips = [
  {
    id: 1,
    category: "Story Inventory",
    tip: "You already tell more stories than you realise; you just haven't mapped them yet. They live in how you introduce yourself, explain your career, or share weekend plans.",
    actionable: "Today, write down three stories you've told in the past week. Notice which life domains they come from."
  },
  {
    id: 2,
    category: "Story Impact",
    tip: "Sensory details make a story easier to picture and remember. Not every detail, just the ones that matter. What did it smell like? Sound like? Feel like?",
    actionable: "Add one vivid sensory detail to your next story. Make listeners feel like they were there with you."
  },
  {
    id: 3,
    category: "Oscillating Narratives",
    tip: "Descending narratives ('everything went wrong') stop at the low point. Oscillating narratives ('it was hard AND I grew') name the struggle and the response. Same facts, different frame.",
    actionable: "Take one struggle story and add the 'AND' clause: what you learned, how you grew, what changed."
  },
  {
    id: 4,
    category: "Story Structure",
    tip: "Module 4 teaches a five-part structure: Setup (where and when), Catalyst (the trigger), Tension (the struggle), Climax (the peak moment), Resolution (what changed).",
    actionable: "Map your next story using the 5 parts. Notice which parts you usually rush or skip entirely."
  },
  {
    id: 5,
    category: "Emotional Arc",
    tip: "Flat emotional arcs bore listeners. Your story should have peaks and valleys: moments of tension and release, struggle and triumph.",
    actionable: "Sketch the emotional journey of your next story. Does it vary enough to keep attention?"
  },
  {
    id: 6,
    category: "Vulnerability",
    tip: "The 10% Rule, this course's own rule of thumb rather than a research finding: share a little beyond your current level of closeness, about 10% deeper. Not too much (oversharing), not too little (shutting down).",
    actionable: "Before sharing a vulnerable story, ask: Is this about 10% deeper than our usual conversations?"
  },
  {
    id: 7,
    category: "Delivery",
    tip: "Filler words ('um,' 'like,' 'you know') can distract from your message when they pile up. The first step is noticing them.",
    actionable: "Record yourself telling a story. Count your filler words without judgement. Just notice the pattern."
  },
  {
    id: 8,
    category: "Story Listening",
    tip: "When someone shares a story, resist redirecting to your own ('Oh, that reminds me of when I...'). Stay with their story first.",
    actionable: "In your next conversation, let someone finish their entire story before sharing yours. Notice the difference."
  },
  {
    id: 9,
    category: "Story Inventory",
    tip: "It's easy to slip into telling mostly descending narratives without noticing. The stories you tell help shape how you see yourself.",
    actionable: "Categorise five stories you tell: descending (decline), ascending (triumph), or oscillating (struggle + growth)."
  },
  {
    id: 10,
    category: "Story Impact",
    tip: "Specific details (dates, names, places) help listeners picture your story and follow it. They ground it in reality.",
    actionable: "Replace vague phrases ('one day,' 'somewhere') with specific details ('Tuesday morning,' 'the café on High Street')."
  },
  {
    id: 11,
    category: "Oscillating Narratives",
    tip: "Psychologist Marshall Duke judged the oscillating family story (ups and downs, and we stuck together) the most healthful of three patterns. The measured finding is a correlation: children who knew more family history tended to have higher self-esteem, a stronger sense of control, better family functioning and lower anxiety (Duke, Lazarus & Fivush, 2008).",
    actionable: "Share one family story that includes both struggle and growth. Model oscillating narratives for those around you."
  },
  {
    id: 12,
    category: "Story Structure",
    tip: "Context-switching is key: your job interview version should emphasise skills; your dinner party version should emphasise humour and connection.",
    actionable: "Take one story and adapt it for two contexts. What do you emphasise differently in each version?"
  },
  {
    id: 13,
    category: "Emotional Arc",
    tip: "Labels ('I was sad') tell; sensory details ('my chest felt tight, my throat burned') let listeners feel it with you.",
    actionable: "Replace one abstract emotion word with a physical sensation. Show the feeling, don't just name it."
  },
  {
    id: 14,
    category: "Vulnerability",
    tip: "Brené Brown puts it simply: shame is 'I am bad'; guilt is 'I did something bad.' In this course, vulnerability calibration means framing stories around what you did and learnt, not around shame.",
    actionable: "Check your vulnerable stories: do they frame you as fundamentally flawed, or as someone who learned from mistakes?"
  },
  {
    id: 15,
    category: "Delivery",
    tip: "Strategic pauses are powerful. They give listeners time to process, build anticipation, and show you're confident enough to hold silence.",
    actionable: "Practise telling a story with three deliberate pauses at key moments. Feel the difference in impact."
  },
  {
    id: 16,
    category: "Story Listening",
    tip: "The urge to fix, minimise, or redirect is strong. But sometimes people just need their story to be received without commentary.",
    actionable: "When someone shares a problem, try 'That sounds really hard' instead of 'Have you tried...?'"
  },
  {
    id: 17,
    category: "Story Inventory",
    tip: "Module 1 maps six story types worth knowing: origin (how you got here), turning point (before and after), high point, low point, redemption (suffering that led somewhere good) and contamination (a good time spoilt by what followed).",
    actionable: "Identify which type each of your go-to stories is. Do you have balance, or are you missing certain types?"
  },
  {
    id: 18,
    category: "Story Impact",
    tip: "Surprise, concrete imagery and repetition all help a story stick. If your story is predictable from the first sentence, it's easier to forget.",
    actionable: "Add one unexpected twist or surprise to your next story. Subvert what listeners expect to happen."
  },
  {
    id: 19,
    category: "Oscillating Narratives",
    tip: "Forced positivity ('everything happens for a reason!') rarely convinces anyone, and a story that stops at the pain leaves out the growth. Oscillating narratives honour both the pain AND the growth.",
    actionable: "When telling a hard story, resist glossing over the struggle. Let the difficulty be real before sharing what you learned."
  },
  {
    id: 20,
    category: "Story Structure",
    tip: "The Climax isn't always the loudest moment. It's the moment of highest stakes. What could you lose? What did you risk?",
    actionable: "Identify the real climax in your next story: the moment of maximum uncertainty or emotional intensity."
  },
  {
    id: 21,
    category: "Emotional Arc",
    tip: "In our experience, meaning-making (eudaemonic) stories create deeper connection than purely entertaining (hedonic) ones. What did this experience mean?",
    actionable: "End your next story with the meaning, not just the outcome. 'And what I realised was...' is your friend."
  },
  {
    id: 22,
    category: "Vulnerability",
    tip: "Context matters enormously. What's appropriate vulnerability at a dinner party is wildly different from a job interview or first date.",
    actionable: "Before sharing a vulnerable story, assess the context: professional, social, intimate, or public speaking?"
  },
  {
    id: 23,
    category: "Delivery",
    tip: "Volume variation, pacing shifts, and strategic emphasis make stories come alive. Monotone delivery kills even the best material.",
    actionable: "Record yourself and listen for variation. Do you modulate volume and pace, or is it all one level?"
  },
  {
    id: 24,
    category: "Story Listening",
    tip: "Carl Rogers: empathic listening means understanding the other person's internal frame of reference. It's not waiting for your turn; it's genuine curiosity.",
    actionable: "In one conversation today, focus entirely on understanding their story. Ask three follow-up questions before sharing."
  },
  {
    id: 25,
    category: "Story Inventory",
    tip: "The stories you tell most often help shape how you see yourself. If you mostly tell descending narratives, check what they leave out: the times you coped, adapted or got help.",
    actionable: "Track what stories you tell this week. Are they empowering, victim-focussed, or growth-oriented?"
  },
  {
    id: 26,
    category: "Story Impact",
    tip: "In our experience, stories draw people closer when they include appropriate vulnerability, universal themes, and resolution. People connect to struggle + growth.",
    actionable: "Include one moment of genuine difficulty in your next story, but make sure there's growth or learning too."
  },
  {
    id: 27,
    category: "Oscillating Narratives",
    tip: "You don't have to pretend the hard parts didn't happen. Oscillating narratives say 'This was genuinely difficult AND this is how I grew.'",
    actionable: "Practise the truth check: Does this feel authentic, or am I forcing positivity onto something still painful?"
  },
  {
    id: 28,
    category: "Story Structure",
    tip: "The 2-Minute Rule, a rule of thumb for casual conversation: if a story runs past 2 minutes, check whether you're losing people. Cut what doesn't serve the core, or break it into conversational chunks.",
    actionable: "Time your signature story. If it's over 2 minutes, identify what can be cut without losing the core."
  },
  {
    id: 29,
    category: "Emotional Arc",
    tip: "Dialogue brings stories to life more than description. Not every conversation verbatim, just the lines that reveal character or advance the story.",
    actionable: "Add one line of dialogue to your next story. Let a character speak instead of summarising what they said."
  },
  {
    id: 30,
    category: "Vulnerability",
    tip: "Vulnerability isn't weakness; it's courage. But calibration is everything. Too much too soon damages trust; too little keeps relationships shallow.",
    actionable: "Share one thing slightly more vulnerable than usual today, calibrated to your relationship. Notice the response."
  }
];

// Get a tip of the day based on the current day
export const getTipOfTheDay = () => {
  const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / 1000 / 60 / 60 / 24);
  const tipIndex = dayOfYear % storytellingTips.length;
  return storytellingTips[tipIndex];
};

// Get a random tip
export const getRandomTip = () => {
  return storytellingTips[Math.floor(Math.random() * storytellingTips.length)];
};

// Get tips by category
export const getTipsByCategory = (category) => {
  return storytellingTips.filter(tip => tip.category === category);
};

// Get all categories
export const getTipCategories = () => {
  return [...new Set(storytellingTips.map(tip => tip.category))];
};

export default storytellingTips;
